
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  PropType,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { MenuItem } from '@/router/nav-menu'

export default defineComponent({
  name: 'HeaderTopMenu',
  props: {
    menuList: {
      type: Array as PropType<MenuItem[]>,
      default: () => [],
    },
    activeLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['select'],
  setup (props, { emit }) {
    const { locale } = useI18n()
    const rootRef = ref<HTMLElement | null>(null)
    const moreMeasureRef = ref<HTMLElement | null>(null)
    const measureRefs = ref<(HTMLElement | null)[]>([])
    const visibleCount = ref(props.menuList.length)

    function setMeasureRef (el: unknown, index: number) {
      measureRefs.value[index] = (el as HTMLElement) || null
    }

    function calcVisibleCount (maxWidth: number, widths: number[], moreWidth: number): number {
      const n = widths.length
      if (!n || maxWidth <= 0) return 0
      const total = widths.reduce((sum, w) => sum + w, 0)
      if (total <= maxWidth) return n

      let used = 0
      let visible = 0
      for (let i = 0; i < n; i++) {
        const itemW = widths[i]
        const remaining = n - i - 1
        const reserve = remaining > 0 ? moreWidth : 0
        if (used + itemW + reserve > maxWidth) break
        used += itemW
        visible = i + 1
      }
      if (visible < n && visible === 0) return 1
      return visible
    }

    function updateLayout () {
      const root = rootRef.value
      if (!root) return
      const maxWidth = root.clientWidth
      const widths = props.menuList.map((_, i) => measureRefs.value[i]?.offsetWidth ?? 0)
      const moreWidth = moreMeasureRef.value?.offsetWidth ?? 56
      visibleCount.value = calcVisibleCount(maxWidth, widths, moreWidth)
    }

    let resizeObserver: ResizeObserver | undefined

    function scheduleUpdate () {
      measureRefs.value = new Array(props.menuList.length).fill(null)
      nextTick(updateLayout)
    }

    const visibleMenus = computed(() => props.menuList.slice(0, visibleCount.value))
    const overflowMenus = computed(() => props.menuList.slice(visibleCount.value))
    const isActiveInOverflow = computed(() =>
      overflowMenus.value.some(item => item.label === props.activeLabel),
    )

    function emitSelect (item: MenuItem) {
      emit('select', item)
    }

    function handleOverflowCommand (label: string) {
      const item = props.menuList.find(m => m.label === label)
      if (item) emitSelect(item)
    }

    onMounted(() => {
      scheduleUpdate()
      if (typeof ResizeObserver !== 'undefined' && rootRef.value) {
        resizeObserver = new ResizeObserver(() => updateLayout())
        resizeObserver.observe(rootRef.value)
      }
      window.addEventListener('resize', updateLayout)
    })

    onUnmounted(() => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateLayout)
    })

    watch(() => props.menuList, scheduleUpdate, { deep: true })
    watch(() => props.activeLabel, () => nextTick(updateLayout))
    watch(locale, scheduleUpdate)

    return {
      rootRef,
      moreMeasureRef,
      setMeasureRef,
      visibleMenus,
      overflowMenus,
      isActiveInOverflow,
      emitSelect,
      handleOverflowCommand,
    }
  },
})
